import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { I18nProvider } from "@/context/I18nContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import Layout from "@/components/Layout";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import Dashboard from "@/pages/Dashboard";
import UploadPrint from "@/pages/UploadPrint";
import BulkUpload from "@/pages/BulkUpload";
import ImagePrint from "@/pages/ImagePrint";
import PassportPhoto from "@/pages/PassportPhoto";
import IdCard from "@/pages/IdCard";
import Connector from "@/pages/Connector";
import MyShop from "@/pages/MyShop";
import ShopWalkin from "@/pages/ShopWalkin";
import CustomerShop from "@/pages/CustomerShop";
import Orders from "@/pages/Orders";
import WalletPage from "@/pages/Wallet";
import Printers from "@/pages/Printers";
import AdminUsers from "@/pages/AdminUsers";
import Settings from "@/pages/Settings";
import Reports from "@/pages/Reports";
import Subscription from "@/pages/Subscription";
import AdminFinance from "@/pages/AdminFinance";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminRetailers from "@/pages/AdminRetailers";
import AdminRetailerDetail from "@/pages/AdminRetailerDetail";
import PvcCutter from "@/pages/PvcCutter";
import FreeTools from "@/pages/FreeTools";
import IdCardA4 from "@/pages/IdCardA4";
import Help from "@/pages/Help";
import About from "@/pages/About";

function LandingGate() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <Navigate to="/dashboard" replace />;
  return <Landing />;
}

// Retailer & customer can access the "Print Studio" pages (retailers use it as their own studio).
const STUDIO_ROLES = ["customer", "retailer", "super_admin"];
const RETAILER_ROLES = ["retailer", "distributor", "super_admin"];

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<LandingGate />} />
      <Route path="/shop/:slug" element={<CustomerShop />} />
      <Route path="/shop-legacy/:slug" element={<ShopWalkin />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />

      <Route path="/dashboard" element={<ProtectedRoute><Layout><Dashboard /></Layout></ProtectedRoute>} />

      {/* Print Studio (customer + retailer) */}
      <Route path="/print" element={<ProtectedRoute roles={STUDIO_ROLES}><Layout><UploadPrint /></Layout></ProtectedRoute>} />
      <Route path="/bulk-print" element={<ProtectedRoute roles={STUDIO_ROLES}><Layout><BulkUpload /></Layout></ProtectedRoute>} />
      <Route path="/image-print" element={<ProtectedRoute roles={STUDIO_ROLES}><Layout><ImagePrint /></Layout></ProtectedRoute>} />
      <Route path="/passport-photo" element={<ProtectedRoute roles={STUDIO_ROLES}><Layout><PassportPhoto /></Layout></ProtectedRoute>} />
      <Route path="/id-card" element={<ProtectedRoute roles={STUDIO_ROLES}><Layout><IdCard /></Layout></ProtectedRoute>} />
      <Route path="/pvc-cutter" element={<ProtectedRoute roles={["retailer", "super_admin"]}><Layout><PvcCutter /></Layout></ProtectedRoute>} />
      <Route path="/free-tools" element={<ProtectedRoute roles={["retailer", "super_admin"]}><Layout><FreeTools /></Layout></ProtectedRoute>} />
      <Route path="/id-card-a4" element={<ProtectedRoute roles={["retailer", "super_admin"]}><Layout><IdCardA4 /></Layout></ProtectedRoute>} />

      <Route path="/orders" element={<ProtectedRoute><Layout><Orders /></Layout></ProtectedRoute>} />
      <Route path="/wallet" element={<ProtectedRoute><Layout><WalletPage /></Layout></ProtectedRoute>} />
      <Route path="/printers" element={<ProtectedRoute roles={["super_admin", "retailer"]}><Layout><Printers /></Layout></ProtectedRoute>} />
      <Route path="/connector" element={<ProtectedRoute roles={RETAILER_ROLES}><Layout><Connector /></Layout></ProtectedRoute>} />
      <Route path="/my-shop" element={<ProtectedRoute roles={["retailer"]}><Layout><MyShop /></Layout></ProtectedRoute>} />
      <Route path="/subscription" element={<ProtectedRoute roles={["retailer", "distributor"]}><Layout><Subscription /></Layout></ProtectedRoute>} />
      <Route path="/reports" element={<ProtectedRoute roles={["super_admin", "distributor", "retailer"]}><Layout><Reports /></Layout></ProtectedRoute>} />

      <Route path="/admin/users" element={<ProtectedRoute roles={["super_admin"]}><Layout><AdminUsers /></Layout></ProtectedRoute>} />
      <Route path="/admin/finance" element={<ProtectedRoute roles={["super_admin"]}><Layout><AdminFinance /></Layout></ProtectedRoute>} />
      <Route path="/admin/dashboard" element={<ProtectedRoute roles={["super_admin"]}><Layout><AdminDashboard /></Layout></ProtectedRoute>} />
      <Route path="/admin/retailers" element={<ProtectedRoute roles={["super_admin"]}><Layout><AdminRetailers /></Layout></ProtectedRoute>} />
      <Route path="/admin/retailers/:id" element={<ProtectedRoute roles={["super_admin"]}><Layout><AdminRetailerDetail /></Layout></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute roles={["super_admin"]}><Layout><Settings /></Layout></ProtectedRoute>} />

      <Route path="/help" element={<ProtectedRoute><Layout><Help /></Layout></ProtectedRoute>} />
      <Route path="/about" element={<ProtectedRoute><Layout><About /></Layout></ProtectedRoute>} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <ThemeProvider>
        <I18nProvider>
          <AuthProvider>
            <BrowserRouter>
              <AppRoutes />
              <Toaster richColors position="top-right" />
            </BrowserRouter>
          </AuthProvider>
        </I18nProvider>
      </ThemeProvider>
    </div>
  );
}
